// NotFoundPage.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, ArrowLeft, AlertTriangle, MapPin, Calendar } from 'lucide-react';

const NotFoundPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-yellow-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-4xl mx-auto text-center">
        {/* Main Content */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 px-8 py-12 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <AlertTriangle className="w-12 h-12" />
                </div>
              </div>
              <h1 className="text-6xl font-bold mb-4">404</h1>
              <h2 className="text-3xl font-semibold mb-2">Page Not Found</h2>
              <p className="text-orange-100 text-lg">
                Oops! The page you're looking for doesn't exist.
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            <div className="max-w-2xl mx-auto">
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                The page you are looking for might have been removed, had its name changed, 
                or is temporarily unavailable. Don't worry, we'll help you get back on track!
              </p>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Link 
                  to="/"
                  className="group bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Home className="w-6 h-6" />
                    </div>
                    <h3 className="font-semibold text-lg">Go Home</h3>
                    <p className="text-orange-100 text-sm">Return to homepage</p>
                  </div>
                </Link>

                <Link 
                  to="/events"
                  className="group bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Calendar className="w-6 h-6" />
                    </div>
                    <h3 className="font-semibold text-lg">Browse Events</h3>
                    <p className="text-blue-100 text-sm">Discover amazing events</p>
                  </div>
                </Link>

                <Link 
                  to="/search"
                  className="group bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Search className="w-6 h-6" />
                    </div>
                    <h3 className="font-semibold text-lg">Search</h3>
                    <p className="text-purple-100 text-sm">Find what you need</p>
                  </div>
                </Link>
              </div>

              {/* Helpful Links */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Popular Pages</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Link 
                    to="/about"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white transition-colors duration-200 group"
                  >
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors duration-200">
                      <MapPin className="w-4 h-4 text-orange-600" />
                    </div>
                    <span className="text-gray-700 group-hover:text-orange-600 transition-colors duration-200">About Us</span>
                  </Link>
                  <Link 
                    to="/contact"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white transition-colors duration-200 group"
                  >
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-200">
                      <MapPin className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-gray-700 group-hover:text-blue-600 transition-colors duration-200">Contact Us</span>
                  </Link>
                  <Link 
                    to="/how-it-works"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white transition-colors duration-200 group"
                  >
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors duration-200">
                      <MapPin className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-700 group-hover:text-green-600 transition-colors duration-200">How It Works</span>
                  </Link>
                  <Link 
                    to="/frequently-asked-questions"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white transition-colors duration-200 group"
                  >
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors duration-200">
                      <MapPin className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="text-gray-700 group-hover:text-purple-600 transition-colors duration-200">FAQ</span>
                  </Link>
                </div>
              </div>

              {/* Back Button */}
              <div className="mt-8">
                <button 
                  onClick={() => window.history.back()}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            If you believe this is an error, please contact our support team.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
